import type { CommissionSightClient } from '@commissionsight/sdk';
import { formatPeriodLong } from '@commissionsight/shared';
import { useCallback } from 'react';
import { type TrendPoint, WeeklyTrend } from '../components/charts.js';
import {
  Badge,
  ErrorNote,
  Metric,
  Panel,
  accountStatusTone,
  jobStatusTone,
} from '../components/ui.js';
import { fmtWhen } from '../lib/format.js';
import { getMarketingSummary, getRegisterBlocks } from '../lib/internal.js';
import { useAsyncData } from '../lib/useAsyncData.js';

export function Metrics({ client }: { client: CommissionSightClient }) {
  const {
    data: m,
    error,
    reload,
  } = useAsyncData(useCallback(() => client.admin.metrics(), [client]));
  const { data: mkt } = useAsyncData(useCallback(() => getMarketingSummary(), []));
  const { data: blk } = useAsyncData(useCallback(() => getRegisterBlocks(), []));

  return (
    <div>
      <Panel title="Overview">
        {error ? (
          <ErrorNote message={error} onRetry={reload} />
        ) : !m ? (
          <p className="text-sm text-neutral-500">Loading…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
              <Metric
                label="Accounts"
                value={m.accounts}
                tone="emerald"
                hint={m.pendingAccounts ? `${m.pendingAccounts} pending` : 'all reviewed'}
              />
              <Metric label="Users" value={m.users} tone="indigo" />
              <Metric label="Total jobs" value={m.totalJobs} tone="sky" />
              <Metric label="Jobs (24h)" value={m.jobsLast24h} tone="sky" />
              <Metric
                label="Failures"
                value={m.failures}
                tone={m.failures > 0 ? 'rose' : 'neutral'}
              />
              <Metric
                label="Webhooks pending"
                value={m.webhooksPending}
                tone={m.webhooksFailed > 0 ? 'rose' : m.webhooksPending > 0 ? 'amber' : 'neutral'}
                hint={m.webhooksFailed ? `${m.webhooksFailed} failed` : undefined}
              />
              <Metric
                label="Bot submits blocked"
                value={blk?.total ?? 0}
                tone={blk && blk.tooFast > 0 ? 'amber' : 'neutral'}
                hint={
                  blk
                    ? `${blk.honeypot} honeypot · ${blk.tooFast} too-fast · ${blk.last7d} in 7d`
                    : 'on /register'
                }
              />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500">
              <span className="font-medium uppercase tracking-wide">Job status</span>
              {(['queued', 'processing', 'completed', 'failed'] as const).map((s) => (
                <span key={s} className="inline-flex items-center gap-1.5">
                  <Badge tone={jobStatusTone(s)}>{s}</Badge>
                  <span className="tabular-nums text-neutral-700">{m.byStatus[s] ?? 0}</span>
                </span>
              ))}
            </div>
            <div className="mt-5 grid gap-4 border-t border-neutral-100 pt-4 lg:grid-cols-2">
              <WeeklyTrend
                title="Weekly active accounts"
                data={weeklyActive(m)}
                tone="emerald"
                caption="Distinct accounts that processed ≥1 statement that week."
              />
              <WeeklyTrend
                title="Statements processed / week"
                data={weeklyJobs(m)}
                tone="sky"
                caption="Ingest volume — total jobs run each week."
              />
            </div>
          </>
        )}
      </Panel>

      {mkt && (
        <Panel title="Marketing outreach">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            <Metric
              label="Campaigns"
              value={mkt.campaigns.total}
              tone="emerald"
              hint={`${mkt.campaigns.active} active · ${mkt.campaigns.draft} draft`}
            />
            <Metric
              label="Leads enrolled"
              value={mkt.leads.enrolled}
              tone="indigo"
              hint={`of ${mkt.leads.total.toLocaleString()} ICP`}
            />
            <Metric
              label="Emails sent"
              value={mkt.messages.sent}
              tone="sky"
              hint={`${mkt.rates.replyRatePct}% reply rate`}
            />
            <Metric
              label="Awaiting reply"
              value={mkt.funnel.sent}
              tone={mkt.funnel.sent > 0 ? 'amber' : 'neutral'}
              hint="sent, no reply yet"
            />
            <Metric label="Replied" value={mkt.funnel.replied} tone="emerald" />
            <Metric
              label="Suppressed"
              value={mkt.suppressions.total}
              tone={mkt.suppressions.total > 0 ? 'rose' : 'neutral'}
              hint={`${mkt.suppressions.bounced} bounced · ${mkt.suppressions.complaints} spam`}
            />
            <Metric
              label="Emails sent today"
              value={mkt.today.sent}
              tone={mkt.today.sent > 0 ? 'sky' : 'neutral'}
              hint="since 00:00 UTC"
            />
            <Metric
              label="Unsubs today"
              value={mkt.today.unsubs}
              tone={mkt.today.unsubs > 0 ? 'amber' : 'neutral'}
              hint="since 00:00 UTC"
            />
            <Metric
              label="Bounced today"
              value={mkt.today.bounced}
              tone={mkt.today.bounced > 0 ? 'rose' : 'neutral'}
              hint="since 00:00 UTC"
            />
            <Metric
              label="Landed total"
              value={mkt.messages.landed}
              tone={mkt.messages.landed > 0 ? 'emerald' : 'neutral'}
              hint="delivered, not bounced/unsubbed"
            />
            <Metric
              label="Landed today"
              value={mkt.today.landed}
              tone={mkt.today.landed > 0 ? 'emerald' : 'neutral'}
              hint="since 00:00 UTC"
            />
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500">
            <span className="font-medium uppercase tracking-wide">Lead funnel</span>
            {[
              { label: 'queued', n: mkt.funnel.queued },
              { label: 'drafted', n: mkt.funnel.drafted },
              { label: 'sent', n: mkt.funnel.sent },
              { label: 'replied', n: mkt.funnel.replied },
              { label: 'bounced', n: mkt.funnel.bounced },
              { label: 'opted out', n: mkt.funnel.unsubscribed },
            ].map((f) => (
              <span key={f.label} className="inline-flex items-center gap-1.5">
                <b className="tabular-nums text-neutral-700">{f.n}</b> {f.label}
              </span>
            ))}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500">
            <span className="font-medium uppercase tracking-wide">Deliverability</span>
            <span
              className="inline-flex items-center gap-1.5"
              title="Share of sent emails Resend confirmed the recipient server accepted. Not proof of inbox placement — use Google Postmaster Tools for that."
            >
              <b className="tabular-nums text-neutral-700">{mkt.rates.deliveredRatePct}%</b>{' '}
              delivered
            </span>
            {mkt.messages.delayed > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <b className="tabular-nums text-neutral-700">{mkt.messages.delayed}</b> delayed
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <b className="tabular-nums text-neutral-700">{mkt.rates.bounceRatePct}%</b> bounce
              rate
            </span>
            <span className="inline-flex items-center gap-1.5">
              <b className="tabular-nums text-neutral-700">{mkt.suppressions.bounced}</b> hard
              bounces
            </span>
            <span className="inline-flex items-center gap-1.5">
              <b className="tabular-nums text-neutral-700">{mkt.suppressions.complaints}</b> spam
              complaints
            </span>
            <span className="inline-flex items-center gap-1.5">
              <b className="tabular-nums text-neutral-700">{mkt.suppressions.optouts}</b> opt-outs
            </span>
            <span className="inline-flex items-center gap-1.5">
              <b className="tabular-nums text-neutral-700">{mkt.messages.drafts}</b> drafts to
              approve
            </span>
          </div>
        </Panel>
      )}

      {m && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Panel title="Recent jobs">
            {m.recentJobs.length === 0 ? (
              <p className="text-sm text-neutral-500">No jobs yet.</p>
            ) : (
              <ul className="divide-y divide-neutral-100 text-sm">
                {m.recentJobs.map((j) => (
                  <li key={j.id} className="flex items-center justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge tone={jobStatusTone(j.status)}>{j.status}</Badge>
                        <span className="text-neutral-700">
                          {formatPeriodLong({ year: j.periodYear, month: j.periodMonth })}
                        </span>
                      </div>
                      {j.error && (
                        <div className="mt-0.5 truncate text-xs text-rose-600">{j.error}</div>
                      )}
                    </div>
                    <span className="shrink-0 text-xs text-neutral-400">
                      {fmtWhen(j.createdAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title="New accounts">
            {m.recentAccounts.length === 0 ? (
              <p className="text-sm text-neutral-500">No accounts yet.</p>
            ) : (
              <ul className="divide-y divide-neutral-100 text-sm">
                {m.recentAccounts.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-3 py-2">
                    <div className="flex items-center gap-2">
                      <Badge tone={accountStatusTone(a.status)}>{a.status}</Badge>
                      <span className="font-medium text-neutral-800">{a.name}</span>
                    </div>
                    <span className="text-xs text-neutral-400">{fmtWhen(a.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      )}
    </div>
  );
}

/** Read the (admin-only) weekly series the metrics endpoint adds, mapped to the
 *  shared TrendPoint shape. Typed locally so the view reads it without an SDK release. */
function weeklyActive(m: unknown): TrendPoint[] {
  const s = (m as { activeAccountsWeekly?: { weekStart: string; activeAccounts: number }[] })
    .activeAccountsWeekly;
  return (s ?? []).map((d) => ({ label: d.weekStart, value: d.activeAccounts }));
}
function weeklyJobs(m: unknown): TrendPoint[] {
  const s = (m as { jobsWeekly?: { weekStart: string; jobs: number }[] }).jobsWeekly;
  return (s ?? []).map((d) => ({ label: d.weekStart, value: d.jobs }));
}
